import { z } from "zod";
import api, { Option, OptionEntity, getBackstageURL } from "services/api";

export const roleSchema = z.object({
  roleNo: z
    .string()
    .regex(/[a-zA-Z0-9]+/, "須為英文或數字")
    .min(1, "群組編號為必填項目")
    .max(2, "最多為2字"),
  roleName: z
    .string()
    .min(1, "群組名稱為必填項目")
    .max(20, "群組名稱最多為20字"),
});

export type Role = z.infer<typeof roleSchema>;

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    roles: build.query<Role[], void>({
      query: () => getBackstageURL("/roles"),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ roleNo }) => ({
                type: "Role" as const,
                id: roleNo,
              })),
              "Role",
            ]
          : ["Role"],
    }),
    role: build.query<Role, string>({
      query: (id) => getBackstageURL(`/roles/${id}`),
      providesTags: (result, error, arg) => [{ type: "Role", id: arg }],
    }),
    roleUsers: build.query<string[], string>({
      query: (id) => getBackstageURL(`/roles/${id}/users`),
      providesTags: ["RoleUser"],
    }),
    roleFunctions: build.query<number[], string>({
      query: (id) => getBackstageURL(`/roles/${id}/functions`),
      providesTags: ["RoleFunction"],
    }),

    createRole: build.mutation<Role, Role>({
      query: (body) => ({
        url: getBackstageURL("/roles"),
        method: "POST",
        body,
      }),
      invalidatesTags: ["Role"],
    }),

    updateRole: build.mutation<void, Role>({
      query: (body) => ({
        url: getBackstageURL(`/roles/${body.roleNo}`),
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Role", id: arg.roleNo }],
    }),
    updateRoleFunctions: build.mutation<
      void,
      { id: string; functions: number[] }
    >({
      query: (body) => ({
        url: getBackstageURL(`/roles/${body.id}/functions`),
        method: "PUT",
        body: body.functions,
      }),
      invalidatesTags: ["Function", "RoleFunction"],
    }),
    updateRoleUsers: build.mutation<void, { id: string; users: string[] }>({
      query: (body) => ({
        url: getBackstageURL(`/roles/${body.id}/users`),
        method: "PUT",
        body: body.users,
      }),
      invalidatesTags: ["RoleUser"],
    }),

    deleteRole: build.mutation<void, string>({
      query: (id) => ({
        url: getBackstageURL(`/roles/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: ["Role"],
    }),

    userEntity: build.query<OptionEntity, void>({
      query: () => getBackstageURL("/roles/user-options"),
      transformResponse: (data: Option[] = []) => {
        const entity: OptionEntity = {
          ids: [],
          entities: {},
        };

        data.forEach((x) => {
          entity.ids.push(x.value);
          entity.entities[x.value] = x;
        });

        return entity;
      },
    }),
    functionEntity: build.query<OptionEntity, void>({
      query: () => getBackstageURL("/roles/function-options"),
      transformResponse: (data: Option[] = []) => {
        const entity: OptionEntity = {
          ids: [],
          entities: {},
        };

        data.forEach((x) => {
          entity.ids.push(x.value);
          entity.entities[x.value] = x;
        });

        return entity;
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useRolesQuery,
  useRoleQuery,
  useRoleUsersQuery,
  useRoleFunctionsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useUpdateRoleFunctionsMutation,
  useUpdateRoleUsersMutation,
  useDeleteRoleMutation,
  useUserEntityQuery,
  useFunctionEntityQuery,
} = extendedApi;
